import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import {
  ArrowLeft,
  Share2,
  MapPin,
  CheckCircle2,
  User,
  Phone,
  Printer,
  ChevronRight,
} from "lucide-react";
import { FaFileWord } from "react-icons/fa";

export default function InvoicePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (!location.state || !location.state.trip) {
      navigate("/dashboard");
    } else {
      setInvoiceData(location.state.trip);
    }
    window.scrollTo(0, 0);
  }, [location, navigate]);

  if (!invoiceData) return null;

  const {
    bookingId = "N/A",
    date = Date.now(),
    customerName = "Guest Customer",
    customerMobile = "N/A",
    pickup = "Pickup Location",
    drop = "Drop-off Location",
    carName = "Assigned Cab",
    totalFare = 0,
    baseFare = 0,
    taxes = 0,
    driver = 0,
  } = invoiceData;

  const formattedDate = new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // --- 1. SHARE AS IMAGE ---
  const handleShare = async () => {
    if (!receiptRef.current) return;
    setIsLoading(true);
    try {
      const header = document.getElementById("patra-print-header");
      if (header) header.style.display = "block";

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      if (header) header.style.display = "none";

      canvas.toBlob(async (blob) => {
        if (!blob) {
          setIsLoading(false);
          return;
        }
        const fileName = `Invoice_${bookingId}.png`;
        const file = new File([blob], fileName, { type: "image/png" });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "Ride Receipt",
            text: "Here is your ride receipt from Patra Travels.",
            files: [file],
          });
        } else {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
        }
        setIsLoading(false);
      }, "image/png");
    } catch (error) {
      console.error("Error capturing/sharing invoice:", error);
      alert("Could not generate the image to share.");
      setIsLoading(false);
    }
  };

  // --- 2. DOWNLOAD PDF ---
  const handleDownloadPDF = () => {
    window.print();
  };

  // --- 3. WORD EXPORT ---
  const handleExportWord = () => {
    setIsLoading(true);
    setTimeout(() => {
      try {
        const wordHtmlTemplate = `
                    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
                    <head>
                        <meta charset='utf-8'>
                        <title>Invoice</title>
                        <style>
                            body { font-family: 'Segoe UI', Arial, sans-serif; color: #333; }
                            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
                            .amount-sec { text-align: center; margin-bottom: 25px; }
                            .table-details { width: 100%; margin-bottom: 20px; border-bottom: 1px solid #ddd; padding-bottom: 15px; }
                            .table-locations { width: 100%; background-color: #f8f9fa; margin-bottom: 25px; }
                            .table-fare { width: 100%; border-collapse: collapse; }
                            .table-fare td { padding: 8px 0; border-bottom: 1px dashed #eee; }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <h1 style="margin: 0 0 10px 0; font-size: 28px; letter-spacing: 1px;">
                                <span style="color: #ff6600; font-weight: bold;">PATRA</span> <span style="color: #000000; font-weight: bold;">TRAVELS PVT. LTD.</span>
                            </h1>
                            <p style="margin: 0; font-size: 11px; line-height: 1.4;">
                                <u><b>Registered Address:</b></u> Plot No. 1151, 1st Floor, Royal Palace Campus, Tankapani Road,<br/>
                                Adjacent to Radhika Tower, Near Rabi Talkies, Bhubaneswar, Odisha - 751018<br/>
                                <u><b>Support No.</b></u> 8337911112, 8337911113
                            </p>
                        </div>
                        <div class="amount-sec">
                            <h2 style="font-size: 32px; margin: 0 0 5px 0; color: #000;">&#8377;${totalFare?.toLocaleString() || "0"}</h2>
                            <p style="color: #198754; font-weight: bold; margin: 0; font-size: 14px;">Payment Successful</p>
                        </div>
                        <table class="table-details">
                            <tr>
                                <td style="width: 50%;"><span style="font-size: 10px; color: #666;">RECEIPT NO.</span><br/><strong>#${bookingId}</strong></td>
                                <td style="width: 50%; text-align: right;"><span style="font-size: 10px; color: #666;">DATE</span><br/><strong>${formattedDate}</strong></td>
                            </tr>
                            <tr>
                                <td style="width: 50%; padding-top: 10px;"><span style="font-size: 10px; color: #666;">CUSTOMER NAME</span><br/><strong>${customerName}</strong></td>
                                <td style="width: 50%; text-align: right; padding-top: 10px;"><span style="font-size: 10px; color: #666;">MOBILE</span><br/><strong>${customerMobile}</strong></td>
                            </tr>
                        </table>
                        <table class="table-locations" cellpadding="12">
                            <tr><td style="border-bottom: 1px solid #eaeaea;"><span style="font-size: 10px; color: #198754; font-weight: bold;">PICKUP</span><br/><span style="font-size: 14px; font-weight: bold;">${pickup}</span></td></tr>
                            <tr><td><span style="font-size: 10px; color: #dc3545; font-weight: bold;">DROP-OFF</span><br/><span style="font-size: 14px; font-weight: bold;">${drop}</span></td></tr>
                        </table>
                        <h3 style="font-size: 14px; color: #666; margin-bottom: 10px; text-transform: uppercase;">Fare Breakdown (${carName})</h3>
                        <table class="table-fare">
                            <tr><td>Trip Fare</td><td style="text-align: right; font-weight: bold;">&#8377;${baseFare?.toLocaleString() || "0"}</td></tr>
                            <tr><td>Taxes & Fees</td><td style="text-align: right; font-weight: bold;">&#8377;${taxes?.toLocaleString() || "0"}</td></tr>
                            <tr><td>Driver Allowance</td><td style="text-align: right; font-weight: bold;">&#8377;${driver?.toLocaleString() || "0"}</td></tr>
                            <tr><td colspan="2"><hr style="border: 0; border-top: 1px solid #333; margin: 10px 0;" /></td></tr>
                            <tr><td style="font-weight: bold; font-size: 16px;">Total Amount Paid</td><td style="text-align: right; font-weight: bold; font-size: 16px; color: #0d6efd;">&#8377;${totalFare?.toLocaleString() || "0"}</td></tr>
                        </table>
                    </body></html>
                `;
        const url =
          "data:application/vnd.ms-word;charset=utf-8," +
          encodeURIComponent(wordHtmlTemplate);
        const fileName = `Invoice_${bookingId}.doc`;
        const downloadLink = document.createElement("a");
        document.body.appendChild(downloadLink);
        if (navigator.msSaveOrOpenBlob) {
          navigator.msSaveOrOpenBlob(
            new Blob(["\ufeff", wordHtmlTemplate], {
              type: "application/msword",
            }),
            fileName,
          );
        } else {
          downloadLink.href = url;
          downloadLink.download = fileName;
          downloadLink.click();
        }
        document.body.removeChild(downloadLink);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }, 100);
  };

  return (
    <div className="ip-root">
      {/* ── SCREEN HEADER ── */}
      <div className="ip-topbar no-print">
        <div className="ip-topbar-left">
          <div className="ip-topbar-icon">
            <CheckCircle2 size={16} color="#16a34a" />
          </div>
          <span className="ip-topbar-title">E-Receipt</span>
        </div>
        <button
          className="ip-share-icon-btn"
          onClick={handleShare}
          aria-label="Share"
        >
          <Share2 size={18} />
        </button>
      </div>

      <div className="ip-scroll-body">
        {/* ── PRINTABLE RECEIPT ── */}
        <div id="printable-invoice" ref={receiptRef}>
          {/* Print-only company header */}
          <div
            id="patra-print-header"
            className="print-only-header"
            style={{
              display: "none",
              textAlign: "center",
              padding: "20px 10px",
              backgroundColor: "white",
              borderBottom: "2px solid #333",
            }}
          >
            <h1
              style={{
                margin: "0 0 10px 0",
                fontSize: "28px",
                fontFamily: "Arial, sans-serif",
                letterSpacing: "1px",
              }}
            >
              <span style={{ color: "#ff6600", fontWeight: "bold" }}>
                PATRA
              </span>{" "}
              <span style={{ color: "#000000", fontWeight: "bold" }}>
                TRAVELS PVT. LTD.
              </span>
            </h1>
            <p
              style={{
                margin: "0",
                fontSize: "11px",
                color: "#000",
                lineHeight: "1.4",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <span style={{ textDecoration: "underline", fontWeight: "bold" }}>
                Registered Address:
              </span>{" "}
              Plot No. 1151, 1st Floor, Royal Palace Campus, Tankapani Road,
              <br />
              Adjacent to Radhika Tower, Near Rabi Talkies, Bhubaneswar, Odisha
              - 751018
              <br />
              <span style={{ textDecoration: "underline", fontWeight: "bold" }}>
                Support No.
              </span>{" "}
              8337911112, 8337911113
            </p>
          </div>

          {/* ── RECEIPT CARD ── */}
          <div className="ip-receipt">
            {/* SUCCESS HERO */}
            <div className="ip-hero">
              <div className="ip-hero-glow" />
              <div className="ip-check-ring">
                <div className="ip-check-inner">
                  <CheckCircle2 size={26} color="#16a34a" strokeWidth={2.5} />
                </div>
              </div>
              <div className="ip-hero-amount">
                <span className="ip-hero-rupee">₹</span>
                <span className="ip-hero-number">
                  {totalFare?.toLocaleString() || "0"}
                </span>
              </div>
              <div className="ip-success-tag">
                <span className="ip-success-dot" />
                Payment Successful
              </div>
            </div>

            {/* TICKET TEAR */}
            <div className="ip-tear">
              <div className="ip-tear-notch ip-tear-notch--left" />
              <div className="ip-tear-line" />
              <div className="ip-tear-notch ip-tear-notch--right" />
            </div>

            {/* BODY */}
            <div className="ip-body">
              {/* BOOKING META GRID */}
              <div className="ip-meta-grid">
                <div className="ip-meta-cell">
                  <span className="ip-meta-label">Receipt No.</span>
                  <span className="ip-meta-value">#{bookingId}</span>
                </div>
                <div className="ip-meta-cell ip-meta-cell--right">
                  <span className="ip-meta-label">Date</span>
                  <span className="ip-meta-value">{formattedDate}</span>
                </div>
                <div className="ip-meta-cell">
                  <span className="ip-meta-label">
                    <User size={10} style={{ marginRight: 3 }} />
                    Customer
                  </span>
                  <span className="ip-meta-value">{customerName}</span>
                </div>
                <div className="ip-meta-cell ip-meta-cell--right">
                  <span className="ip-meta-label">
                    <Phone size={10} style={{ marginRight: 3 }} />
                    Mobile
                  </span>
                  <span className="ip-meta-value">{customerMobile}</span>
                </div>
              </div>

              {/* ROUTE CARD */}
              <div className="ip-route-card">
                <div className="ip-route-row">
                  <div className="ip-route-dot ip-route-dot--pickup" />
                  <div className="ip-route-info">
                    <span className="ip-route-label">Pickup</span>
                    <span className="ip-route-place">{pickup}</span>
                  </div>
                </div>
                <div className="ip-route-connector">
                  <div className="ip-route-line" />
                </div>
                <div className="ip-route-row">
                  <div className="ip-route-dot ip-route-dot--drop" />
                  <div className="ip-route-info">
                    <span className="ip-route-label">Drop-off</span>
                    <span className="ip-route-place">{drop}</span>
                  </div>
                </div>
              </div>

              {/* FARE BREAKDOWN */}
              <div className="ip-fare-section">
                <div className="ip-fare-header">
                  <span className="ip-fare-title">Fare Breakdown</span>
                  <span className="ip-car-badge">{carName}</span>
                </div>

                <div className="ip-fare-row">
                  <span className="ip-fare-label">Trip Fare</span>
                  <span className="ip-fare-value">
                    ₹{baseFare?.toLocaleString() || "0"}
                  </span>
                </div>
                <div className="ip-fare-row">
                  <span className="ip-fare-label">Taxes & Fees</span>
                  <span className="ip-fare-value">
                    ₹{taxes?.toLocaleString() || "0"}
                  </span>
                </div>
                <div className="ip-fare-row">
                  <span className="ip-fare-label">Driver Allowance</span>
                  <span className="ip-fare-value">
                    ₹{driver?.toLocaleString() || "0"}
                  </span>
                </div>

                <div className="ip-fare-total-row">
                  <span className="ip-fare-total-label">Total Paid</span>
                  <span className="ip-fare-total-value">
                    ₹{totalFare?.toLocaleString() || "0"}
                  </span>
                </div>
              </div>
            </div>
            {/* END BODY */}
          </div>
          {/* END RECEIPT CARD */}
        </div>
        {/* END PRINTABLE */}

        {/* ── ACTION BUTTONS ── */}
        <div className="ip-actions no-print">
          <div className="ip-actions-row">
            <button
              className="ip-btn ip-btn--pdf"
              onClick={handleDownloadPDF}
              disabled={isLoading}
            >
              <Printer size={18} />
              <span>Print / PDF</span>
            </button>
            <button
              className="ip-btn ip-btn--word"
              onClick={handleExportWord}
              disabled={isLoading}
            >
              <FaFileWord size={18} />
              <span>Export Word</span>
            </button>
          </div>
          <button
            className="ip-btn ip-btn--share"
            onClick={handleShare}
            disabled={isLoading}
          >
            <Share2 size={19} />
            <span>{isLoading ? "Processing…" : "Share Receipt"}</span>
            <ChevronRight size={17} className="ip-btn-arrow" />
          </button>
        </div>
      </div>
      {/* END SCROLL BODY */}

      {/* ── LOADING OVERLAY ── */}
      {isLoading && (
        <div className="ip-overlay">
          <div className="ip-overlay-spinner" />
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@500&display=swap');

                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                .ip-root {
                    font-family: 'DM Sans', sans-serif;
                    background: #EAEDF2;
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── TOPBAR ── */
                .ip-topbar {
                    position: sticky; top: 0; z-index: 50;
                    background: rgba(255,255,255,0.92);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    border-bottom: 1px solid rgba(0,0,0,0.06);
                    padding: 14px 18px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .ip-topbar-left { display: flex; align-items: center; gap: 10px; }
                .ip-topbar-icon {
                    width: 30px; height: 30px;
                    border-radius: 50%;
                    background: #dcfce7;
                    display: flex; align-items: center; justify-content: center;
                }
                .ip-topbar-title { font-size: 16px; font-weight: 800; color: #0f172a; letter-spacing: -0.2px; }
                .ip-share-icon-btn {
                    background: #f1f5f9; border: none; cursor: pointer;
                    width: 36px; height: 36px; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: #334155; transition: background 0.15s;
                }
                .ip-share-icon-btn:active { background: #e2e8f0; transform: scale(0.95); }

                /* ── SCROLL BODY ── */
                .ip-scroll-body {
                    flex: 1;
                    padding: 20px 16px 48px;
                    max-width: 480px;
                    width: 100%;
                    margin: 0 auto;
                }

                /* ── RECEIPT CARD ── */
                .ip-receipt {
                    background: #fff;
                    border-radius: 28px;
                    overflow: visible;
                    box-shadow:
                        0 1px 2px rgba(0,0,0,0.04),
                        0 8px 32px rgba(0,0,0,0.08),
                        0 24px 60px rgba(0,0,0,0.06);
                    position: relative;
                }

                /* ── HERO ── */
                .ip-hero {
                    background: linear-gradient(160deg, #0f172a 0%, #1e3a5f 100%);
                    border-radius: 28px 28px 0 0;
                    padding: 36px 24px 28px;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                }
                .ip-hero-glow {
                    position: absolute;
                    width: 200px; height: 200px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%);
                    top: -60px; left: 50%; transform: translateX(-50%);
                    pointer-events: none;
                }
                .ip-check-ring {
                    width: 64px; height: 64px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.08);
                    border: 1.5px solid rgba(255,255,255,0.12);
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 18px;
                }
                .ip-check-inner {
                    width: 48px; height: 48px;
                    border-radius: 50%;
                    background: #fff;
                    display: flex; align-items: center; justify-content: center;
                }
                .ip-hero-amount {
                    display: flex; align-items: baseline;
                    justify-content: center; gap: 3px;
                    margin-bottom: 12px;
                }
                .ip-hero-rupee {
                    font-size: 22px; font-weight: 700;
                    color: rgba(255,255,255,0.65);
                }
                .ip-hero-number {
                    font-size: 46px; font-weight: 800;
                    color: #fff;
                    letter-spacing: -2px;
                    line-height: 1;
                }
                .ip-success-tag {
                    display: inline-flex; align-items: center; gap: 7px;
                    background: rgba(34,197,94,0.15);
                    border: 1px solid rgba(34,197,94,0.3);
                    color: #4ade80;
                    font-size: 12.5px; font-weight: 700;
                    padding: 6px 14px;
                    border-radius: 50px;
                    letter-spacing: 0.2px;
                }
                .ip-success-dot {
                    width: 7px; height: 7px;
                    border-radius: 50%;
                    background: #4ade80;
                    box-shadow: 0 0 6px #4ade80;
                    flex-shrink: 0;
                    animation: pulse-dot 2s ease-in-out infinite;
                }
                @keyframes pulse-dot {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }

                /* ── TICKET TEAR ── */
                .ip-tear {
                    position: relative;
                    height: 1px;
                    background: transparent;
                    display: flex; align-items: center;
                    margin: 0;
                    overflow: visible;
                    z-index: 2;
                }
                .ip-tear-notch {
                    position: absolute;
                    width: 28px; height: 28px;
                    border-radius: 50%;
                    background: #EAEDF2;
                    top: 50%; transform: translateY(-50%);
                    z-index: 3;
                    flex-shrink: 0;
                }
                .ip-tear-notch--left { left: -14px; }
                .ip-tear-notch--right { right: -14px; }
                .ip-tear-line {
                    flex: 1;
                    border-top: 2px dashed #e2e8f0;
                    margin: 0 22px;
                }

                /* ── BODY ── */
                .ip-body { padding: 22px 22px 26px; }

                /* META GRID */
                .ip-meta-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    row-gap: 16px;
                    column-gap: 8px;
                    margin-bottom: 20px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #f1f5f9;
                }
                .ip-meta-cell { display: flex; flex-direction: column; gap: 4px; }
                .ip-meta-cell--right { text-align: right; align-items: flex-end; }
                .ip-meta-label {
                    font-size: 10px; font-weight: 700;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                    display: flex; align-items: center;
                }
                .ip-meta-value {
                    font-size: 13.5px; font-weight: 700;
                    color: #0f172a;
                    font-family: 'DM Mono', monospace;
                }
                .ip-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }

                /* ROUTE CARD */
                .ip-route-card {
                    background: #f8fafc;
                    border: 1px solid #e9eef5;
                    border-radius: 18px;
                    padding: 16px 18px 14px;
                    margin-bottom: 20px;
                }
                .ip-route-row { display: flex; align-items: flex-start; gap: 12px; }
                .ip-route-dot {
                    width: 12px; height: 12px; border-radius: 50%;
                    flex-shrink: 0; margin-top: 4px;
                }
                .ip-route-dot--pickup { background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.15); }
                .ip-route-dot--drop   { background: #dc2626; box-shadow: 0 0 0 3px rgba(220,38,38,0.15); }
                .ip-route-connector {
                    display: flex; justify-content: flex-start;
                    padding: 5px 0 5px 5px;
                }
                .ip-route-line {
                    width: 2px; height: 18px;
                    background: repeating-linear-gradient(
                        to bottom,
                        #cbd5e1 0px, #cbd5e1 4px,
                        transparent 4px, transparent 8px
                    );
                    margin-left: 5px;
                }
                .ip-route-info { display: flex; flex-direction: column; gap: 3px; flex: 1; }
                .ip-route-label {
                    font-size: 10px; font-weight: 700;
                    text-transform: uppercase; letter-spacing: 0.7px;
                    color: #94a3b8;
                }
                .ip-route-place {
                    font-size: 13px; font-weight: 700;
                    color: #1e293b; line-height: 1.4;
                }

                /* FARE BREAKDOWN */
                .ip-fare-section { }
                .ip-fare-header {
                    display: flex; justify-content: space-between;
                    align-items: center; margin-bottom: 14px;
                }
                .ip-fare-title {
                    font-size: 10px; font-weight: 800;
                    color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px;
                }
                .ip-car-badge {
                    font-size: 11px; font-weight: 700;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    color: #475569;
                    padding: 4px 10px; border-radius: 50px;
                }
                .ip-fare-row {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 9px 0;
                    border-bottom: 1px solid #f8fafc;
                }
                .ip-fare-label { font-size: 13px; font-weight: 500; color: #64748b; }
                .ip-fare-value { font-size: 13px; font-weight: 700; color: #334155; }

                .ip-fare-total-row {
                    display: flex; justify-content: space-between; align-items: center;
                    margin-top: 14px;
                    padding: 14px 16px;
                    background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
                    border-radius: 14px;
                }
                .ip-fare-total-label {
                    font-size: 13.5px; font-weight: 700; color: rgba(255,255,255,0.75);
                }
                .ip-fare-total-value {
                    font-size: 20px; font-weight: 800; color: #fff;
                    letter-spacing: -0.5px;
                }

                /* ── ACTION BUTTONS ── */
                .ip-actions { margin-top: 20px; display: flex; flex-direction: column; gap: 10px; }
                .ip-actions-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

                .ip-btn {
                    display: flex; align-items: center; justify-content: center; gap: 8px;
                    padding: 15px 18px;
                    border-radius: 16px;
                    font-size: 13.5px; font-weight: 700;
                    font-family: 'DM Sans', sans-serif;
                    border: none; cursor: pointer;
                    transition: transform 0.15s, opacity 0.15s;
                    position: relative; overflow: hidden;
                }
                .ip-btn:active { transform: scale(0.97); }
                .ip-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                .ip-btn--pdf {
                    background: #eff6ff;
                    color: #1d4ed8;
                    border: 1.5px solid #bfdbfe;
                }
                .ip-btn--word {
                    background: #f5f3ff;
                    color: #6d28d9;
                    border: 1.5px solid #ddd6fe;
                }
                .ip-btn--share {
                    background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
                    color: #fff;
                    box-shadow: 0 4px 20px rgba(15,23,42,0.25);
                    padding: 17px 22px;
                    font-size: 15px;
                }
                .ip-btn-arrow { margin-left: auto; opacity: 0.6; }

                /* ── LOADING OVERLAY ── */
                .ip-overlay {
                    position: fixed; inset: 0;
                    background: rgba(255,255,255,0.75);
                    backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 9999;
                }
                .ip-overlay-spinner {
                    width: 44px; height: 44px;
                    border: 4px solid #e2e8f0;
                    border-top-color: #0f172a;
                    border-radius: 50%;
                    animation: ip-spin 0.7s linear infinite;
                }
                @keyframes ip-spin { to { transform: rotate(360deg); } }

                /* ── PRINT ── */
                @media print {
                    body * { visibility: hidden; }
                    @page { size: auto; margin: 10mm; }
                    html, body { height: 100%; margin: 0 !important; padding: 0 !important; background-color: white !important; }
                    #printable-invoice, #printable-invoice * { visibility: visible; }
                    #printable-invoice { position: absolute; left: 0; top: 0; width: 100%; background-color: white !important; }
                    .print-only-header { display: block !important; }
                    .ip-receipt { box-shadow: none !important; border: 1px solid #ccc !important; border-radius: 0 !important; }
                    .ip-tear-notch { display: none !important; }
                    .no-print, header, footer, .bottom-nav, .navbar { display: none !important; }
                }
            `}</style>
    </div>
  );
}
